import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/system';
import SearchBox from '../../components/formlib/SearchBox';
import TitleText from '../../components/formlib/TitleText';
import {
  AlertColor,
  debounce,
  Paper,
  Table,
  TableBody,
  TableContainer,
} from '@mui/material';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import IFSPReviewListRow from './IFSPReviewListRow';
import { Order } from '../referral/ReferralDashboard';
import { getIfspReviewData } from '../../services/ifspClient/ifspReviewApi';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import AppPagination from '../../components/shared/AppPagination';
import { LookUpContext } from '../../context/LookUpContextProvider';
import {
  getAllReferrals,
  getFacesheetDetails,
  getListByRole,
  ReferralResponseType,
} from '../../services/configApi/Clients';
import {
  CLIENT_NAME_KEY,
  EST_DATETIME_REGEX,
  LOCAL_DATE_TIME_FORMAT,
  LOCAL_DATE_TIME_UTC_FORMAT,
  REFERRAL_ID_KEY,
  REFERRAL_PROGRAM_KEY,
  REFERRAL_STATUS_KEY,
} from '../../services/Constant';
import moment from 'moment';
import { useNavigate } from 'react-router';
import { NEW_IFSP_ROUTE_NAME } from '../../routes/Routing';
import { OptionType } from '../../utils/type';
import dayjs from 'dayjs';
import { formatName } from '../../utils/nameUtils';
import SearchNotFound from '../../components/formlib/SearchNotFound';
import EmptyScreen from '../../components/shared/EmptyScreen';
import { isCustomError, ApiError } from '../../services/ApiResponseHandler';

export interface IfspReviewItem {
  name: string;
  enrollmentNumber: string;
  referralId: string;
  ifspId: string;
  ifspFirstMeetingDate: string; // or Date if you parse it
  serviceCoordinator: string;
  clientId: string;
  emrUpdatedOn?: string;
}

export interface IfspReviewResponse {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  count: number;
  ifsp: IfspReviewItem[];
}

type IFSPReviewListType = {
  ifspId: string;
  clientId: string;
  referralId: string;
  name: string;
  enrollmentNumber: string;
  ifspFirstMeetingDate: string;
  serviceCoordinator: string;
  emrUpdatedOn?: string;
  agingIndays?: number;
  actions?: string;
};

export const initialIfspResponse: IfspReviewResponse = {
  page: 0,
  size: 0,
  totalElements: 0,
  totalPages: 0,
  count: 0,
  ifsp: [],
};

const IFSPReviewScreen = () => {
  const [ifspReviewListData, setIfspReviewListData] =
    useState<IfspReviewResponse>(initialIfspResponse);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof IFSPReviewListType>('emrUpdatedOn');
  const [page, setPage] = useState<number>(0);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState<string>('');
  const [toastrId, setToastrId] = useState<string>('');
  const [serviceCoordinatorList, setServiceCoordinatorList] = useState<
    OptionType[]
  >([]);
  const navigate = useNavigate();
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { updateDemographics, lookupsDemographics } = useContext(LookUpContext);
  function createHeadCell(
    id: keyof IFSPReviewListType,
    labelId: string,
    defaultLabelId: string,
    requiredSorting: boolean,
  ): HeadCell<IFSPReviewListType> {
    return {
      id,
      labelId,
      defaultLabelId,
      numeric: false,
      requiredSorting,
    };
  }

  const HeadCells: HeadCell<IFSPReviewListType>[] = [
    createHeadCell('clientId', 'Dashboard.ClientName', 'Client Name', true),
    createHeadCell(
      'enrollmentNumber',
      'Facesheet.DPHID',
      'Enrollment ID',
      true,
    ),
    createHeadCell(
      'ifspFirstMeetingDate',
      'IFSPReview.ifspFirstMeetingDate',
      'IFSP First Meeting Date',
      true,
    ),
    createHeadCell(
      'serviceCoordinator',
      'IFSPReview.serviceCoordinator',
      'Service Coordinator',
      true,
    ),
    createHeadCell(
      'emrUpdatedOn',
      'ProgressNotesList.emrUpdatedOn',
      'Updated On',
      true,
    ),
    createHeadCell(
      'agingIndays',
      'ProgressNotesList.agingInDays',
      'Aging in Days',
      false,
    ),
    createHeadCell(
      'actions',
      'ProgressNotesList.listHeaderAction',
      'Action',
      false,
    ),
  ];

  const isValidInput = (value: string) => {
    const allowedCharacters = /^[a-zA-Z0-9,'.\s!-]*$/;
    return allowedCharacters.test(value);
  };

  const handleSearchChange = useCallback(
    debounce((value: string) => {
      if (isValidInput(value)) {
        setPage(0);
        setSearchKeyword(value);
      } else {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('Dashboard.ClientList.searchError');
        setToastrDefaultMessage(
          'Only alphanumeric characters, commas, periods, spaces, exclamation marks, and hyphens are allowed to be searched.',
        );
      }
    }, 500),
    [],
  );

  const fetchFaceSheetDetails = async (clientId: string) => {
    if (clientId) {
      toggleLoader(true);
      await getFacesheetDetails(clientId)
        .then((response) => {
          if (response) {
            // update the re-evaluation data
            const data = response;
            const journeyCounts: { [key: string]: number } = {};

            data.clientJourney.forEach((item) => {
              // Count the occurrences of each journey name
              journeyCounts[item.journeyName] =
                (journeyCounts[item.journeyName] || 0) + 1;

              // If it's the 2nd or subsequent occurrence of "backend.journey_name.evaluation", update to "backend.journey_name.re-evaluation"
              if (
                item.journeyName === 'backend.journey_name.evaluation' &&
                journeyCounts[item.journeyName] > 1
              ) {
                item.journeyName = 'backend.journey_name.re-evaluation';
              }
            });

            data.demographics.consentToAccessStartDate =
              response.demographics.consentToAccessStartDate === null &&
              response.demographics.eicsConsentToAccessInsurance === 0
                ? response.demographics.referralDate
                : response.demographics.consentToAccessStartDate;
            data.clientFiles.forEach((file) => {
              if (
                file.tags.timestamp &&
                EST_DATETIME_REGEX.test(file.tags.timestamp)
              ) {
                file.tags.timestamp = dayjs(
                  file.tags.timestamp,
                  'MM-DD-YYYY HH:MM.SS',
                ).format(LOCAL_DATE_TIME_FORMAT);
              } else {
                file.tags.timestamp = moment(
                  file.tags.timestamp,
                  LOCAL_DATE_TIME_UTC_FORMAT,
                )
                  .utc()
                  .local()
                  .format(LOCAL_DATE_TIME_FORMAT);
              }
            });
            updateDemographics(data.demographics);

            const referralsData = localStorage.getItem('clientReferralDetails');
            const referrals = referralsData
              ? (JSON.parse(referralsData) as ReferralResponseType[])
              : [];
            for (const referral of referrals) {
              if (referral.referralId === response.demographics.referralId) {
                localStorage.setItem(REFERRAL_PROGRAM_KEY, referral.programId);
                break;
              }
            }
            localStorage.setItem(
              REFERRAL_STATUS_KEY,
              response.demographics.status,
            );
          }
          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          setOpen(true);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('Client.error');
            setToastrDefaultMessage(error);
          }
        });
    }
  };

  const handleViewClick = async (clickedData: IFSPReviewListType) => {
    localStorage.setItem('ClientId', clickedData.clientId);
    localStorage.setItem(CLIENT_NAME_KEY, clickedData.name);

    if (lookupsDemographics) {
      updateDemographics({
        ...lookupsDemographics,
        firstName: '',
        middleName: '',
        lastName: '',

        suffix: '',
        ageInMonths: null,
        sex: null,
        dateOfBirth: '',
        dphId: null,
      });
    }
    localStorage.setItem(REFERRAL_ID_KEY, clickedData.referralId);

    localStorage.setItem('referralId', clickedData.referralId);
    localStorage.setItem('ClientId', clickedData.clientId);
    localStorage.setItem('clientName', clickedData.name);

    toggleLoader(true);
    if (clickedData.clientId) {
      await getAllReferrals(clickedData.clientId, clickedData.referralId)
        .then(async (response: ReferralResponseType[]) => {
          if (response) {
            localStorage.setItem(
              'clientReferralDetails',
              JSON.stringify(response),
            );

            await fetchFaceSheetDetails(clickedData.clientId);
            navigate(NEW_IFSP_ROUTE_NAME, { state: { from: 'IFSPReview' } });
          }
        })
        .catch((error) => {
          toggleLoader(false);
          setOpen(true);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('clientLoadError');
            setToastrDefaultMessage('Failed to get client details');
          }
        });
    }
  };

  const latestRequestRef = useRef(0);

  useEffect(() => {
    const requestId = ++latestRequestRef.current;
    toggleLoader(true);
    getIfspReviewData(page, 10, orderBy, order, searchKeyword)
      .then((response) => {
        if (requestId !== latestRequestRef.current) {
          return; // Ignore stale response
        }
        const uniqueServiceCoordinatorIds: string[] = [
          ...new Set(
            response.ifsp
              .map((ifspData) => ifspData.serviceCoordinator)
              .filter((id) => id && id.trim() !== ''),
          ),
        ];
        getListByRole(
          'backend.role.service_coordinator',
          uniqueServiceCoordinatorIds,
        )
          .then((res) => {
            setServiceCoordinatorList(() => {
              return res.map((item) => {
                return {
                  id: item.id,
                  label: formatName(item.firstName, '', item.lastName, ''),
                };
              });
            });
            toggleLoader(false);
          })
          .catch((error) => {
            toggleLoader(false);
            setOpen(true);
            if (isCustomError(error)) {
              const apiError = error as ApiError;
              setToastrId(apiError.id);
              setToastrDefaultMessage(apiError.message);
            } else {
              setToastrVariable('error');
              setToastrId('IFSPReview.ListLoadFail');
              setToastrDefaultMessage(
                'Failed to load Service Coordinator List',
              );
            }
          });
        setIfspReviewListData(response);
      })
      .catch((error) => {
        toggleLoader(false);
        setOpen(true);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrVariable('error');
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrVariable('error');
          setToastrId('IFSPReview.ListLoadFail');
          setToastrDefaultMessage('Failed to load IFSP Review List');
        }
      });
  }, [page, orderBy, order, searchKeyword]);

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof IFSPReviewListType,
  ) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClose = (reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Box component={'main'} className="pt-lg">
      <SnackBarComponent
        open={open}
        handleClose={handleClose}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <Box component={'div'} className="flex__justify__space-between pb-lg">
        <TitleText label="IFSP TBR Review" />
        <SearchBox
          labelId="Clientdashboard.searchText"
          defaultlabel="Search Client"
          onChange={(value: string) => handleSearchChange(value)}
        />
      </Box>
      {/* Searching but no results */}
      {searchKeyword !== '' && ifspReviewListData.totalElements === 0 && (
        <SearchNotFound />
      )}

      {/* No records to review */}
      {searchKeyword === '' && ifspReviewListData.totalElements === 0 && (
        <EmptyScreen
          titleLabelId="IFSPReview.noRecordsTitle"
          defaultTitleText="No records to review."
          showButton={false}
        />
      )}

      {ifspReviewListData.totalElements > 0 && (
        <Box component={'div'}>
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHeader
                className="listDataTableHead"
                headerNames={HeadCells}
                order={order}
                orderBy={orderBy}
                checkBoxRequired={false}
                onRequestSort={handleRequestSort}
              />
              <TableBody className="tableRowcss">
                {ifspReviewListData.ifsp.map((row: IFSPReviewListType) => (
                  <IFSPReviewListRow
                    key={row.ifspId}
                    data={row}
                    serviceCoordinator={
                      serviceCoordinatorList.find(
                        (item) => item.id === row.serviceCoordinator,
                      )?.label || ''
                    }
                    onViewClick={(data: IFSPReviewListType) =>
                      handleViewClick(data)
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {ifspReviewListData.totalElements > 10 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <AppPagination
                pageNumber={page}
                paginationCount={ifspReviewListData.totalElements}
                handleChangePage={(_event, newPage) => {
                  if (newPage - 1 !== page) {
                    setPage(newPage - 1);
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default IFSPReviewScreen;
